import {API_VERSION, WS_URL} from "./http/env";
import {decode, encode} from "notepack.io";

const reloadPage = () => {
  window.location.reload();
};

const [getBackoffDuration, resetBackoffDuration] = (() => {
  const INITIAL_TIMEOUT_MS = 100;
  const MAX_TIMEOUT_MS = 1000;
  const BACKOFF_FACTOR = 2;
  const JITTER = 0.5;

  let attempts = 0;

  // Return the backoff duration.
  const duration = () => {
    const timeout = INITIAL_TIMEOUT_MS * BACKOFF_FACTOR ** attempts++;
    const rand = Math.random();
    const deviation = Math.floor(rand * JITTER * timeout);
    const ms = rand < 0.5 ? timeout - deviation : timeout + deviation;
    return Math.min(ms, MAX_TIMEOUT_MS);
  };

  const reset = () => {
    attempts = 0;
  };

  return [duration, reset];
})();

const getWebsocketURL = (brandId: number) => {
  let url = WS_URL.startsWith("http") ? WS_URL.replace(/^http/, "ws") : WS_URL;
  url = `${url.replace(/\/+$/, "")}/ws?v=${API_VERSION}`;
  url = `${url}&t=${Date.now()}`;
  return brandId && brandId > 0 ? `${url}&brand=${brandId}` : url;
};

type AckError = {
  code: string;
  error: true;
  message: string;
};

type AckResponse<R> = R | AckError;

export const isAckError = <R>(res: AckResponse<R>): res is AckError =>
  res && (res as AckError).error;

type AckCallback<R> = (res: AckResponse<R>) => void;

type Handler = (...args: any[]) => void;

type CustomSocket = {
  reconnect: (brandId?: number) => void;
  close: () => void;
  on: (event: string, handler: Handler) => void;
  off: (event: string, handler: Handler) => void;
  emit: {
    <R = unknown>(ev: string, ack?: AckCallback<R>): void;
    <R = unknown>(
      ev: string,
      data?: object | string | number,
      ack?: AckCallback<R>,
    ): void;
  };
};

const nextAckId = (() => {
  let id = 0;
  return () => `_ack_${++id}`;
})();

const createSocket = (() => {
  let ws: WebSocket;
  let reconnectTimeout: ReturnType<typeof setTimeout> | undefined;
  let skipReconnect = false;
  const events: Record<string, Set<Handler>> = {};
  const acks: Record<string, AckCallback<any>> = {};

  /**
   * Buffer for packets that will be sent once the socket is connected
   */
  const sendBuffer: any[] = [];

  const close = () => {
    skipReconnect = true;
    ws.close();
  };

  const on = (event: string, handler: Handler) => {
    if (!events[event]) events[event] = new Set([handler]);
    else events[event].add(handler);
  };

  const off = (event: string, handler: Handler) => {
    events[event].delete(handler);
    if (events[event].size === 0) delete events[event];
  };

  const sendOrQueuePacket = (packet: any[]) => {
    const encoded = encode(packet);
    if (ws && ws.readyState === WebSocket.OPEN) ws.send(encoded);
    else sendBuffer.push(encoded);
  };

  type DataToSend = {
    ev: string;
    data?: object | string | number;
    ack?: AckCallback<any>;
  };

  const send = ({ev, data, ack}: DataToSend) => {
    if (ack) {
      const ackId = nextAckId();
      acks[ackId] = ack;
      sendOrQueuePacket([ev, data, ackId]);
    } else if (data !== undefined && data !== null) {
      sendOrQueuePacket([ev, data]);
    } else {
      sendOrQueuePacket([ev]);
    }
  };

  function emit<R = unknown>(ev: string, ack?: AckCallback<R>): void;
  function emit<R = unknown>(
    ev: string,
    data?: object | string | number,
    ack?: AckCallback<R>,
  ): void;
  function emit<R = unknown>(ev: string, data?: any, ack?: AckCallback<R>) {
    const args = arguments.length;
    if (args === 1) return send({ev});

    if (args === 2)
      return typeof data === "function"
        ? send({ev, ack: data})
        : send({ev, data});

    if (args === 3) {
      if (typeof ack !== "function")
        throw new Error("Ack callback must be a function");
      return send({ev, data, ack});
    }

    throw new Error("Invalid number of arguments passed to socket.emit");
  }

  const disconnect = () => {
    if (!ws || ws.readyState === WebSocket.CLOSED) return;
    emit("disconnect");
    ws.close();
  };

  const runHandlers = (eventName: string, ...args: any[]) => {
    if (events[eventName])
      for (const handler of events[eventName]) handler(...args);
  };

  const SERVER_PING_INTERVAL = 5000; // 5 sec
  let pingTimeout: ReturnType<typeof setTimeout> | undefined;

  const heartbeat = () => {
    clearTimeout(pingTimeout);
    pingTimeout = setTimeout(disconnect, SERVER_PING_INTERVAL + 1000);
  };

  const onMessage = (event: MessageEvent) => {
    const data = decode(new Uint8Array(event.data));
    const [eventName, ...args] = data;

    if (eventName === "ping") {
      heartbeat();
      send({ev: "pong"});
      return;
    }

    // Check if this is an ack response
    if (acks[eventName]) {
      const ackCallback = acks[eventName];
      delete acks[eventName];
      ackCallback(args[0]);
      return;
    }

    // Otherwise, it's a regular event
    runHandlers(eventName, ...args);
  };

  const onOpen = () => {
    heartbeat();

    resetBackoffDuration();
    clearTimeout(reconnectTimeout);
    reconnectTimeout = undefined;
    skipReconnect = false;

    // Send any buffered packets
    while (sendBuffer.length > 0) ws.send(sendBuffer.shift());

    runHandlers("connect");
  };

  const onClose = (event: CloseEvent) => {
    // Outdated client version, force reload
    if (event.code === 4001) {
      disconnect();
      reloadPage();
      return;
    }

    clearTimeout(pingTimeout);
    runHandlers("disconnect");

    if (skipReconnect || reconnectTimeout) return;

    reconnectTimeout = setTimeout(() => {
      reconnectTimeout = undefined;
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      reconnect();
    }, getBackoffDuration());
  };

  const onError = (event: Event) => {
    console.error("WebSocket error: ", event);
  };

  const addListeners = () => {
    if (!ws) return;
    ws.addEventListener("message", onMessage);
    ws.addEventListener("open", onOpen);
    ws.addEventListener("close", onClose);
    ws.addEventListener("error", onError);
  };

  const removeListeners = () => {
    if (!ws) return;
    ws.removeEventListener("message", onMessage);
    ws.removeEventListener("open", onOpen);
    ws.removeEventListener("close", onClose);
    ws.removeEventListener("error", onError);
  };

  let _brandId = -1;

  function init() {
    ws = new WebSocket(getWebsocketURL(_brandId));
    ws.binaryType = "arraybuffer";
    addListeners();
  }

  const reconnect = (brandId?: number) => {
    if (typeof brandId === "number") {
      if (brandId === _brandId) return;
      _brandId = brandId;
    }

    removeListeners();
    disconnect();
    init();
  };

  return (): CustomSocket => ({
    reconnect,
    close,
    emit,
    on,
    off,
  });
})();

const socket = createSocket();
export default socket;

/** Triggered by the server to request users to reload the page */
socket.on("update", () => {
  socket.close();
  reloadPage();
});
